// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from './api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [permissions, setPermissions] = useState(null);

  const loadPermissions = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/users/me/permissions`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPermissions(data);
      }
    } catch (error) {
      console.error('Failed to load permissions:', error);
    }
  }, []);

  const loadUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await authAPI.getMe();
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
      // Load permissions after user is loaded
      await loadPermissions();
    } catch (error) {
      console.error('Failed to load user:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, [token, loadPermissions]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    
    if (response.data.requires_2fa) {
      return response.data;
    }
    
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const verify2FA = async (email, code) => {
    const response = await authAPI.verify2FA({ email, code });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const register = async (data) => {
    const response = await authAPI.register(data);
    
    // Check if the account needs approval (no token returned)
    if (response.data.is_approved === false) {
      // Don't store token or user - account pending approval
      return response.data;
    }
    
    // Account is approved - proceed with login
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    setPermissions(null);
  };

  const isAdmin = user?.role === 'admin';
  const isTrainer = user?.role === 'trainer';
  const isManager = user?.role === 'manager';
  const isCandidate = user?.role === 'candidate';
  const canManage = isAdmin || isTrainer || isManager;
  const canCreateWorkspace = permissions?.can_create_workspace || false;

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      permissions,
      login,
      verify2FA,
      register,
      logout,
      loadPermissions,
      isAdmin,
      isTrainer,
      isManager,
      isCandidate,
      canManage,
      canCreateWorkspace,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
